import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconOfflineSale, IconPass, IconTicket } from '@components/icons';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import {
  OFFLINE_SALES_SCOPE_EVENT,
  OFFLINE_SALES_SCOPE_PASS,
  OfflineSalesForm,
  OfflineSalesScope,
} from '@components/process-offline-sales/src/process-offline-sales.type';

import { ControlledMultiOfferSelector } from '../multi-offer/controlled-multi-offer-selector';
import { createMultiSelectorOption } from '../utils/create-multi-selector-option';

export const SelectScope = ({ onContinue }: { onContinue: () => void }) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<OfflineSalesForm>();
  const scope = watch('scope');

  const toggleScope = (next: OfflineSalesScope) => {
    if (scope === next) return;
    setValue('scope', next, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    if (next === OFFLINE_SALES_SCOPE_PASS) {
      setValue('eventId', '');
      setValue('offerId', '');
      setValue('saleType', '');
    } else {
      setValue('passId', '');
    }
  };

  const scopeOptions = useMemo(
    () => [
      createMultiSelectorOption({
        id: OFFLINE_SALES_SCOPE_EVENT,
        name: t('offers:offlineSalesScopeEventTitle'),
        subtitle: t('offers:offlineSalesScopeEventSubtitle'),
        icon: IconTicket,
        value: scope,
        toggler: toggleScope,
      }),
      createMultiSelectorOption({
        id: OFFLINE_SALES_SCOPE_PASS,
        name: t('offers:offlineSalesScopePassTitle'),
        subtitle: t('offers:offlineSalesScopePassSubtitle'),
        icon: IconPass,
        value: scope,
        toggler: toggleScope,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, scope]
  );

  return (
    <>
      <ModalHeader title={t('offers:offlineSalesTitle')} primaryIcon={IconOfflineSale} />
      <div className='flex flex-col gap-4 px-4 py-6 sm:px-20 sm:pb-12'>
        <p className='text-body-m text-secondary'>{t('offers:offlineSalesScopeStepDescription')}</p>
        <ControlledMultiOfferSelector formName='scope' typeOptions={scopeOptions} type={scope} />
      </div>
      <ModalFooter
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={onContinue}
        isPrimaryDisabled={!scope}
      />
    </>
  );
};
